import { useAdversarialStore, getSelectedRun } from '../../stores/useAdversarialStore';
import type { FailureType } from '../../editor/adversarialEngine';
import styles from './Adversarial.module.css';

const FAILURE_TYPE_CLASS: Record<FailureType, string> = {
  insufficient_balance: styles.failureTypeInsufficient,
  invalid_flow: styles.failureTypeInvalid,
  failed_condition: styles.failureTypeCondition,
  value_mismatch: styles.failureTypeMismatch,
  split_inconsistency: styles.failureTypeSplit,
  unreachable_execution: styles.failureTypeFlow,
  incomplete_flow: styles.failureTypeFlow,
  overflow: styles.failureTypeInsufficient,
  rounding_error: styles.failureTypeSplit,
};

const FAILURE_TYPE_LABEL: Record<FailureType, string> = {
  insufficient_balance: 'Insufficient Balance',
  invalid_flow: 'Invalid Flow',
  failed_condition: 'Failed Condition',
  value_mismatch: 'Value Mismatch',
  split_inconsistency: 'Split Inconsistency',
  unreachable_execution: 'Unreachable Execution',
  incomplete_flow: 'Incomplete Flow',
  overflow: 'Overflow',
  rounding_error: 'Rounding Error',
};

export function ResultsView() {
  const state = useAdversarialStore();
  const { result, selectedScenario, selectedRunId, selectRun, selectScenario } = state;
  const selectedRun = getSelectedRun(state);

  if (!result || selectedScenario === null) {
    return (
      <div className={styles.emptyState}>
        <div className={styles.emptyIcon}>◇</div>
        <div className={styles.emptyText}>Select a scenario to view results</div>
      </div>
    );
  }

  const scenario = result.scenarios.find(s => s.category === selectedScenario);
  if (!scenario) {
    return (
      <div className={styles.emptyState}>
        <div className={styles.emptyText}>Run this scenario first</div>
      </div>
    );
  }

  return (
    <div>
      {/* Back to scenarios */}
      <div className={styles.traceHeader}>
        <span className={styles.traceTitle}>{scenario.category.replace(/_/g, ' ')}</span>
        <button className={styles.backBtn} onClick={() => selectScenario(null)}>
          ← All Scenarios
        </button>
      </div>

      {/* Results table */}
      <table className={styles.resultsTable}>
        <thead>
          <tr>
            <th>Run</th>
            <th>Status</th>
            <th>Failures</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {scenario.runs.map(run => (
            <tr
              key={run.id}
              className={`${styles.resultRow} ${selectedRunId === run.id ? styles.resultRowSelected : ''}`}
              onClick={() => selectRun(run.id)}
            >
              <td title={run.label}>{run.label}</td>
              <td>
                {run.failures.length > 0 ? (
                  <span className={styles.statusFailed}>FAIL</span>
                ) : (
                  <span className={styles.statusSuccess}>PASS</span>
                )}
              </td>
              <td>{run.failures.length > 0 ? run.failures.length : '—'}</td>
              <td>
                <button
                  className={styles.viewBtn}
                  onClick={(e) => { e.stopPropagation(); selectRun(run.id); }}
                >
                  View
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Failure details for selected run */}
      {selectedRun && selectedRun.failures.length > 0 && (
        <>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionTitle}>Failure Details</span>
          </div>
          <ul className={styles.failureList}>
            {selectedRun.failures.map((f, i) => (
              <li key={i} className={styles.failureItem}>
                <div className={`${styles.failureType} ${FAILURE_TYPE_CLASS[f.type] ?? ''}`}>
                  {FAILURE_TYPE_LABEL[f.type] ?? f.type}
                </div>
                {f.nodeId !== null && (
                  <div className={styles.failureNode}>Node #{f.nodeId}</div>
                )}
                <div className={styles.failureExplanation}>{f.explanation}</div>
                {f.suggestedFix && (
                  <div className={styles.failureFix}>Fix: {f.suggestedFix}</div>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
